import React from 'react';
import { Page } from 'react-pdf';
import { CanvasPath as SketchCanvasPath, ReactSketchCanvasProps } from 'react-sketch-canvas';
import { ControllerRenderProps } from 'react-hook-form';
import classNames from 'classnames';
import TextAreaContainer from './TextAreaContainer';
import LineContainer from './LineContainer';
import InternalLinkTooltip from './InternalLinkTooltip';
import SymbolsModal from './SymbolSelector/SymbolsModal';
import { usePDFReaderContext } from './PDFReaderContext';
import { usePointerInteractions, useSymbolSelectorModal, useClickOutsideModal } from '@punchcard/core/hooks';
import { WIPMode } from '@punchcard/core/constants/Wip';

type CanvasComponentProps = ReactSketchCanvasProps & {
	mode?: WIPModeType;
	index: number;
	isLoading?: boolean;
	statusCode?: string;
	isActivityOpen?: boolean;
	handleAddHistory: (index: number) => void;
};

interface LinkState {
	tooltipPosition: { x: number; y: number };
	showTooltip: boolean;
	linkId: number | null;
	linkPage: number | null;
}

interface PDFPageInteractiveProps {
	pageIndex: number;
	pageWidth: number;
	field: ControllerRenderProps<WIPForm, `studentLayer.${number}`>;
	pageNumberArray: number[];
	linkState: LinkState;
	handleInternalLinkClick?: (id: number, pageNumber: number) => void;
	hideLinkTooltip: () => void;
	onPageRenderSuccess: () => void;
	statusCode?: string;
	isActivityOpen: boolean;
	size: number;
	colour: string;
	eraserSize: number;
	addHistory: (index: number) => void;
	CanvasComponent: React.ComponentType<CanvasComponentProps>;
	setIsDirty?: (isDirty: boolean) => void;
	featureFlagSymbolSelector?: boolean;
}

const PDFPageInteractive: React.FC<PDFPageInteractiveProps> = ({
	pageIndex,
	pageWidth,
	field,
	pageNumberArray,
	linkState,
	handleInternalLinkClick,
	hideLinkTooltip,
	onPageRenderSuccess,
	statusCode,
	isActivityOpen,
	size,
	colour,
	eraserSize,
	addHistory,
	CanvasComponent,
	setIsDirty,
	featureFlagSymbolSelector
}) => {	// Get PDF Reader state from context
	const { mode, setActiveTextBox, setActiveLine } = usePDFReaderContext();

	// Symbol selector modal logic using custom hooks
	const {
		showSymbolsModal,
		activeTextAreaElement,
		handleSymbolSelect,
		toggleSymbolsModal,
		closeModal
	} = useSymbolSelectorModal({ mode });

	// Handle clicking outside modal to close it
	useClickOutsideModal({
		isModalOpen: showSymbolsModal,
		activeTextAreaElement,
		onClose: closeModal
	});

	// Use the consolidated pointer interactions hook directly here where the events are handled
	const { handlePointerDown, handlePointerMove, handlePointerUp, handleTypeClick, handleLineClick } = usePointerInteractions({
		mode,
		setActiveTextBox,
		setActiveLine,
		setIsDirty
	});
	const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
		// Handle double-click events for type and line modes
		if (e.detail === 2) {
			// Check if the click is coming from within any modal or overlay
			const target = e.target as HTMLElement;
			const isFromModal = target.closest('.symbols-modal') !== null ||
				target.closest('.modal') !== null;

			// Don't create text areas or lines if the click is from within a modal
			if (isFromModal) {
				return;
			}

			if (mode === WIPMode.Type) {
				handleTypeClick(e, pageIndex, field.value, field.onChange);
			} else if (mode === WIPMode.Line) {
				handleLineClick(e, pageIndex, field.value, field.onChange);
			}
		}
	};

	return (
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
		<div
			key={`page_${pageIndex + 1}`}
			className={classNames('position-relative h-100 w-100', mode === WIPMode.Hand ? 'cursor-grab' : 'prevent-select')}
			onPointerDown={handlePointerDown}
			onPointerMove={handlePointerMove}
			onMouseDown={handlePointerDown}
			onPointerUp={handlePointerUp}
			onPointerCancel={handlePointerUp}
			onPointerLeave={handlePointerUp}
			onClick={handleClick}
		>
			<Page
				key={`page_${pageIndex + 1}`}
				width={pageWidth}
				pageNumber={pageIndex + 1}
				onRenderSuccess={onPageRenderSuccess}
			/>

			{/* Internal Link Tooltip */}
			<InternalLinkTooltip
				isVisible={linkState.showTooltip}
				position={linkState.tooltipPosition}
				linkId={linkState.linkId}
				linkPage={linkState.linkPage}
				onNavigate={(id, page) => handleInternalLinkClick?.(id, page)}
				onClose={hideLinkTooltip}
			/>
			{/* Text Areas Layer */}
			<div className="position-absolute top-0 left-0 w-100 h-100 right-0 bottom-0 divPage">
				<div className="d-flex flex-fill position-relative">
					{field.value?.typing?.map((text) => (
						<TextAreaContainer
							key={text.id}
							text={text}
							pageIndex={pageIndex}
							field={field}
							firstPage={pageIndex + 1 === pageNumberArray[0]}
							lastPage={pageIndex + 1 === pageNumberArray[pageNumberArray.length - 1]}
							toggleSymbolsModal={toggleSymbolsModal}
							featureFlagSymbolSelector={featureFlagSymbolSelector ?? false}
						/>
					))}
				</div>
			</div>

			{/* Lines Layer */}
			<div className="position-absolute top-0 left-0 w-100 h-100 right-0 bottom-0 divPage">
				<div className="d-flex flex-fill position-relative">
					{field.value?.lines?.map((line) => (
						<LineContainer
							key={line.id}
							line={line}
							pageIndex={pageIndex}
							field={field}
						/>
					))}
				</div>
			</div>
			{/* Canvas Layer */}
			<CanvasComponent
				className="position-absolute top-0 left-0 w-100 h-100 start-0 end-0 pdf-canvas-layer"
				id={`canvas-${pageIndex}`}
				value={field.value?.paths as SketchCanvasPath[] | undefined}
				allowOnlyPointerType="all"
				strokeWidth={size}
				strokeColor={colour}
				statusCode={statusCode}
				isActivityOpen={isActivityOpen}
				canvasColor="transparent"
				eraserWidth={eraserSize}
				mode={mode}
				index={pageIndex}
				handleAddHistory={addHistory}
				onChange={(v: SketchCanvasPath[]) => {
					field.onChange({ ...field.value, paths: v });
				}}
			/>
			{showSymbolsModal && (
				<SymbolsModal
					isOpen={showSymbolsModal}
					onClose={closeModal}
					onSymbolSelect={handleSymbolSelect}
				/>
			)}
		</div>
	);
};

export default PDFPageInteractive;
